import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import EventDetail from "@/domain/entities/emails/EventDetail";
import LinkObject from "@/domain/entities/emails/LinkObject";
import TodoItem from "@/domain/entities/TodoItem";
import { computeDedupKey } from "@cs_teamwaydev/mailvista-sdk_react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// **************************************************
// Form utils
// **************************************************

export function verifyEmail(email: string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email);
}

export function verifyPhone(phone: string) {
    const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

    return phoneRegex.test(phone);
}

export function verifySiret(siret: string) {
    const trimmedSiret = siret.replace(/\s/g, "");
    if (!trimmedSiret) return false;

    const siretRegex = /^\d{14}$/;
    if (!siretRegex.test(trimmedSiret)) return false;

    return true;
}

// **************************************************
// Mails list utils
// **************************************************

// Mirror: apps/mobile/src/utils/utils.ts — corps identique, toute évolution vaut des deux côtés.
export const getTodosFromMailsList = (mails: Email[]): TodoItem[] => {
    if (mails.length === 0) {
        return [];
    }

    // Show todos from the most recent ANALYZED mail (one with aiDetails).
    // Replies/forwards are not analyzed for the current user → skipping them prevents
    // hiding the todos of the original analyzed mail behind an unanalyzed reply.
    const latestAnalyzedMail = [...mails]
        .sort((a, b) => b.sentDateTime.getTime() - a.sentDateTime.getTime())
        .find((mail) => mail.aiDetails);

    return latestAnalyzedMail?.aiDetails?.toDo ?? [];
};

export const getDocumentsFromMailsList = (
    mails: Email[],
): EmailAttachment[] => {
    const allAttachments = mails
        .flatMap((currentMail) => currentMail.attachments)
        .filter(Boolean) as EmailAttachment[];

    const downloadableAttachments = allAttachments.filter(
        (attachment) => !attachment.isEmbeddedInBody(),
    );

    const uniqueAttachments = new Map<string, EmailAttachment>();

    downloadableAttachments.forEach((attachment) => {
        const key = attachment.contentId
            ? `byContentId:${attachment.contentId}`
            : `byNameType:${attachment.name}|${attachment.type}`;
        if (!uniqueAttachments.has(key)) {
            uniqueAttachments.set(key, attachment);
        }
    });

    return Array.from(uniqueAttachments.values());
};

export const getLinksFromMailsList = (mails: Email[]): LinkObject[] => {
    const links = mails
        .flatMap((mail) => mail.links ?? [])
        .filter(Boolean) as LinkObject[];

    // Dédup sur clé normalisée (même normalisation que le SDK : casse host, slash final,
    // fragment, params de tracking) pour éviter les doublons cosmétiques entre mails.
    const seenUrls = new Map<string, LinkObject>();
    links.forEach((link) => {
        if (!link.url) {
            return;
        }
        const key = computeDedupKey(link.url);
        if (!seenUrls.has(key)) {
            seenUrls.set(key, link);
        }
    });

    return Array.from(seenUrls.values());
};

/**
 * Returns true if the event is past (end date/time is before now).
 */
export function isEventPast(event: AgendaItem | EventDetail): boolean {
    if ("end" in event) {
        return new Date() > event.end;
    }

    return event.endDateTime != null && new Date() > event.endDateTime;
}

export function getEventsFromMailsList(mails: Email[]): EventDetail[] {
    return mails
        .flatMap((currentMail) => currentMail.linkedEventDetails)
        .filter((event) => !!event && !isEventPast(event)) as EventDetail[];
}
